import { useCallback, useRef } from "react";
import {
  clampToTier,
  resolveExerciseTracker,
  type ExerciseTrackerChoice,
  type StoredExerciseTracker,
  type TrackerRange,
} from "@heraion/shared";

import { useOptimisticSave } from "../useOptimisticSave";

/** Host seam: persist the tracker's choice on the profile (mobile
 *  patchProfile, web useProfileDoc), resolving once the profile carries it. */
export type SaveExerciseTracker = (next: StoredExerciseTracker) => Promise<unknown>;

// #389: what the tracker shows, applied at once (useOptimisticSave). No
// `save`: the choice holds for the visit only. The range comes first: it
// decides which sessions load, and the default exercise is read off them,
// so `choiceWith` takes that default once the panel has it. A free account
// reads its tier's choice (clampToTier, #392); the stored value is untouched.
export function useExerciseTracker(
  stored: StoredExerciseTracker | undefined,
  save: SaveExerciseTracker | undefined,
  premium: boolean,
): {
  range: TrackerRange;
  choiceWith: (fallbackSlug: string | null) => ExerciseTrackerChoice;
  saveFailed: boolean;
  update: (op: (c: ExerciseTrackerChoice) => ExerciseTrackerChoice, fallbackSlug: string | null) => void;
} {
  const { draft, saveFailed, commit } = useOptimisticSave<StoredExerciseTracker, StoredExerciseTracker>(save);
  const current = draft ?? stored;
  const latest = useRef(current);
  latest.current = current;

  const choiceWith = useCallback(
    (fallbackSlug: string | null) => clampToTier(resolveExerciseTracker(current, fallbackSlug), premium),
    [current, premium],
  );

  /** Applies a change to the whole resolved choice and saves all of it, so
   *  a default the athlete has kept becomes his choice from then on. */
  const update = useCallback(
    (op: (c: ExerciseTrackerChoice) => ExerciseTrackerChoice, fallbackSlug: string | null) => {
      const next = op(resolveExerciseTracker(latest.current, fallbackSlug));
      latest.current = next;
      commit(next, next);
    },
    [commit],
  );

  return { range: choiceWith(null).range, choiceWith, saveFailed, update };
}
